import { Plus, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { MetricConfig, MetricFilterType, MetricType } from '@intone/shared/types'

interface MetricsConfigTableProps {
  metrics: MetricConfig[]
  onMetricsChange: (metrics: MetricConfig[]) => void
}

const filterOptions: { value: MetricFilterType; label: string }[] = [
  { value: 'all_calls', label: 'All calls' },
  { value: 'successful_calls', label: 'Successful calls' },
  { value: 'over_30_seconds', label: 'Calls over 30 seconds' },
]

const typeOptions: { value: MetricType; label: string }[] = [
  { value: 'includes_phrase', label: 'Includes phrase' },
  { value: 'llm_eval', label: 'LLM eval' },
]

export function MetricsConfigTable({
  metrics,
  onMetricsChange,
}: MetricsConfigTableProps) {
  const addMetric = () => {
    const newMetric: MetricConfig = {
      id: Date.now().toString(),
      name: '',
      filter: 'all_calls',
      type: 'includes_phrase',
      config: {
        phrases: '',
      },
    }
    onMetricsChange([...metrics, newMetric])
  }

  const removeMetric = (id: string) => {
    onMetricsChange(metrics.filter((m) => m.id !== id))
  }

  const updateMetric = (id: string, updates: Partial<MetricConfig>) => {
    onMetricsChange(
      metrics.map((m) => (m.id === id ? { ...m, ...updates } : m))
    )
  }

  return (
    <Card>
      <CardHeader className="border-b py-2">
        <CardTitle className="text-body">Post-Call Analysis</CardTitle>
      </CardHeader>
      <CardContent className="p-4 grid gap-4">
        {metrics.length === 0 ? (
          <div className="text-center py-6 text-body text-muted-foreground">
            No metrics configured. Click "Add Metric" to create one.
          </div>
        ) : (
          <div className="grid gap-4 grid-cols-[repeat(auto-fit,_minmax(560px,_1fr))]">
            {metrics.map((metric) => (
              <div
                key={metric.id}
                className="relative border rounded-lg p-3 bg-muted/50"
              >
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeMetric(metric.id)}
                  className="absolute top-2 right-2 h-7 w-7 p-0 text-gray-400 hover:text-destructive"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>

                <div className="grid grid-cols-2 items-start gap-3 pr-8">
                  <label className="text-detail font-medium text-gray-600 grid gap-1">
                    Metric Name
                    <Input
                      value={metric.name}
                      onChange={(e) =>
                        updateMetric(metric.id, { name: e.target.value })
                      }
                      placeholder="e.g., Greeting Check, Quality Score"
                      className="h-8"
                    />
                  </label>

                  <label className="text-detail font-medium text-gray-600 grid gap-1">
                    Metric Type
                    <Select
                      value={metric.type}
                      onValueChange={(value: MetricType) => {
                        const newConfig =
                          value === 'includes_phrase'
                            ? { phrases: '' }
                            : { prompt: '' }
                        const newFilter =
                          value === 'llm_eval' &&
                          metric.filter === 'all_calls'
                            ? 'successful_calls'
                            : metric.filter
                        updateMetric(metric.id, {
                          type: value,
                          config: newConfig,
                          filter: newFilter,
                        })
                      }}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {typeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>

                  <label className="text-detail font-medium text-gray-600 grid gap-1">
                    Apply to
                    <Select
                      value={metric.filter}
                      onValueChange={(value: MetricFilterType) =>
                        updateMetric(metric.id, { filter: value })
                      }
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {filterOptions.map((option) => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>

                  <div className="grid gap-1">
                    {metric.type === 'includes_phrase' ? (
                      <>
                        <label className="text-detail font-medium text-gray-600 grid gap-1">
                          Phrases (comma-separated)
                          <Input
                            value={metric.config.phrases || ''}
                            onChange={(e) =>
                              updateMetric(metric.id, {
                                config: {
                                  ...metric.config,
                                  phrases: e.target.value,
                                },
                              })
                            }
                            placeholder="e.g., thank you, have a great day, goodbye"
                            className="h-8"
                          />
                        </label>
                        <p className="text-detail text-gray-500">
                          Checks if any of these phrases appear in the
                          transcript
                        </p>
                      </>
                    ) : (
                      <>
                        <label className="text-detail font-medium text-gray-600 grid gap-1">
                          LLM Prompt
                          <Textarea
                            value={metric.config.prompt || ''}
                            onChange={(e) =>
                              updateMetric(metric.id, {
                                config: {
                                  ...metric.config,
                                  prompt: e.target.value,
                                },
                              })
                            }
                            placeholder="e.g., Did the agent greet the customer professionally? Answer YES or NO."
                            className="min-h-[80px]"
                          />
                        </label>
                        <p className="text-detail text-gray-500">
                          This prompt will be used to evaluate the call
                          transcript
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <Button
          variant="outline"
          onClick={addMetric}
          className="w-full sm:w-auto justify-self-start"
        >
          <Plus className="w-3 h-3" />
          Add Metric
        </Button>
      </CardContent>
    </Card>
  )
}